import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, ArrowLeft, Edit, Package, CreditCard, Mail, History, Upload, Pen, Receipt, Download, Eye, Loader2, Link2 } from "lucide-react";
import { orderApi, propertyApi, emailApi, payoutApi } from "@/services/api";
import { downloadInvoicePdf } from "@/utils/invoicePdfUtils";
import { useToast } from "@/components/ui/use-toast";
import { Order, OrderStatus, OrderDocument } from "@/models";
import { StatusUpdateModal } from "@/components/orders/StatusUpdateModal";
import { PaymentsTab } from "@/components/orders/PaymentsTab";
import { EmailHistoryTab } from "@/components/orders/EmailHistoryTab";
import { OrderItemsTab } from "@/components/orders/OrderItemsTab";
import { RequestedDocumentsTab } from "@/components/orders/RequestedDocumentsTab";
import { OrderHistoryTab } from "@/components/orders/OrderHistoryTab";
import { AttachmentsTab } from "@/components/orders/AttachmentsTab";
import { OrderNotesTab } from "@/components/orders/OrderNotesTab";
import { supabase } from "@/integrations/supabase/client";
import { downloadStorageFile, newUploadTarget } from "@/utils/storageRefs";
import * as React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DialogFixUtil } from "@/components/ui/dialog-fix-util";
import { DEFAULT_SENDER_EMAIL } from "@/services/api/emailApi";
import { RefundModal } from "@/components/orders/RefundModal";
import { apiRequest, SUPABASE_URL } from "@/services/api/apiUtils";
import { portalOrigin } from "@/utils/portalUrl";

const getBadgeVariant = (status: string) => {
  switch (status) {
    case "approved":
      return "success";
    case "under_review":
    case "application_submitted":
      return "warning";
    case "pending_payment":
      return "secondary";
    case "draft":
      return "secondary";
    case "denied":
    case "rejected":
      return "destructive";
    default:
      return "outline";
  }
};

const getStatusText = (status: string) => {
  switch (status) {
    case "approved":
      return "Approved";
    case "under_review":
      return "Under Review";
    case "application_submitted":
      return "Submitted";
    case "pending_payment":
      return "Pending Payment";
    case "denied":
    case "rejected":
      return "Denied";
    default:
      return status.replace(/_/g, " ");
  }
};

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, hasPermission, getCurrentRoleIds, selectedRole } = useAuth();
  const [isStatusModalOpen, setIsStatusModalOpen] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState("items");
  const [attachments, setAttachments] = React.useState([]);
  const [loadingAttachments, setLoadingAttachments] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [statusModalFile, setStatusModalFile] = React.useState<File | null>(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isSavingItems, setIsSavingItems] = useState(false);
  const [openingBuyerView, setOpeningBuyerView] = useState(false);
  const [copyingReadOnlyLink, setCopyingReadOnlyLink] = useState(false);
  const queryClient = useQueryClient();

  /**
   * Open this order the way the buyer sees it.
   *
   * Reading the database tells you what was saved; it does not tell you what
   * the buyer is looking at. This asks the backend for a read-only pass
   * scoped to this order and opens the buyer site with it, so the same pages
   * render the same data, running the buyer's own code — the only way to see
   * a wrong total or a step that will not let them through (Mauro, 15-Sep).
   *
   * The pass lasts 30 minutes, covers this order alone, and cannot write.
   */
  const requestBuyerViewUrl = async (scope: "read" | "write") => {
    const { token } = await apiRequest<{ token: string }>(
      `${SUPABASE_URL}/orders/${order.id}/support-session`,
      { method: "POST", body: { scope } }
    );
    return `${portalOrigin()}/order/${order.id}?support=${encodeURIComponent(token)}`;
  };

  const openBuyerView = async () => {
    if (!order?.id) return;
    // Opened before the await, because a window opened after one is no longer
    // tied to the click and the browser blocks it as a popup. No "noopener"
    // here: it makes window.open return null, and then there is nothing to
    // navigate — which is how this first shipped, leaving a blank tab behind
    // and hijacking the one you were on.
    const tab = window.open("", "_blank");
    try {
      setOpeningBuyerView(true);
      const url = await requestBuyerViewUrl("write");
      if (tab) {
        tab.location.replace(url);
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      tab?.close();
      toast({
        variant: "destructive",
        title: "Could not open the buyer's view",
        description: error?.message || "Please try again.",
      });
    } finally {
      setOpeningBuyerView(false);
    }
  };

  /**
   * The link to hand to customer support: same screens, but it cannot change
   * anything. Copied rather than opened — the point is to send it on.
   */
  const copyReadOnlyLink = async () => {
    if (!order?.id) return;
    try {
      setCopyingReadOnlyLink(true);
      const url = await requestBuyerViewUrl("read");
      await navigator.clipboard.writeText(url);
      toast({
        title: "Read-only link copied",
        description:
          "It opens this order on the buyer site, changes nothing, and expires in 30 minutes.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Could not create the read-only link",
        description: error?.message || "Please try again.",
      });
    } finally {
      setCopyingReadOnlyLink(false);
    }
  };

  const handleSaveItems = async (
    updatedItems: { id: number; quantity: number; subtotal_amount: number }[]
  ) => {
    if (!id) return;
    setIsSavingItems(true);
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/orders/${id}/items`;
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ items: updatedItems }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to update items');
      }
      toast({ title: 'Items updated', description: 'Order items and total have been recalculated.' });
      queryClient.invalidateQueries({ queryKey: ['backoffice-order', id] });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to update items', variant: 'destructive' });
    } finally {
      setIsSavingItems(false);
    }
  };

  console.log("Order ID from URL params:", id);
  console.log("Current authenticated user:", user);

  // Permission check function for orders write access
  const canWriteOrders = () => {
    const currentRoleIds = getCurrentRoleIds();
    return hasPermission(currentRoleIds, 'write', 'orders');
  };

  const { data: order, isLoading, error, refetch } = useQuery({
    queryKey: ['backoffice-order', id],
    queryFn: async () => {
      if (!id) throw new Error("Order ID is missing");
      
      const orderData = await orderApi.getBackofficeOrder(Number(id));
      
      if (orderData.property_id) {
        try {
          const propertyData = await propertyApi.getById(orderData.property_id);
          orderData.property = propertyData;
        } catch (propertyError) {
          console.error("Error fetching property:", propertyError);
        }
      }

      if (orderData.customer_id) {
        try {
          const { data: customerData } = await supabase
            .from('customers')
            .select('*')
            .eq('id', orderData.customer_id)
            .single();
          
          if (customerData) {
            orderData.customer = customerData;
          }
        } catch (customerError) {
          console.error("Error fetching customer:", customerError);
        }
      }
      
      return orderData;
    },
    enabled: !!id
  });

  React.useEffect(() => {
    async function fetchAttachments() {
      if (!id) return;
      setLoadingAttachments(true);
      console.log("Fetching attachments for order ID:", id);
      
      const { data, error } = await supabase
        .from("order_attachments")
        .select("*")
        .eq("order_id", parseInt(id))
        .order("created_at", { ascending: false });
      
      if (error) {
        console.error("Error fetching attachments:", error);
      } else {
        console.log("Attachments fetched:", data);
        setAttachments(data || []);
      }
      setLoadingAttachments(false);
    }
    fetchAttachments();
  }, [id]);

  const handleStatusUpdate = async (
    newStatus: string,
    note?: string,
    notifyCustomer: boolean = false,
    file?: File | null
  ) => {
    if (!id) return;

    setUploading(true);
    try {
      let uploadedAttachment = null;
      let fileDataBuffer: ArrayBuffer | null = null;

      if (file) {
        // Private bucket, non-guessable name; the row stores the canonical
        // storage:// ref, opened later through storage-url.
        const { path: filePath, ref: fileRef } = newUploadTarget("order_files", `order_${id}`, file);
        const { error: uploadError } = await supabase.storage
          .from("order_files")
          .upload(filePath, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: attachData, error: attachError } = await supabase
          .from("order_attachments")
          .insert({
            order_id: parseInt(id),
            file_path: fileRef,
            file_name: file.name,
          })
          .select()
          .single();

        if (attachError) {
          throw attachError;
        }

        setAttachments((prev) => [attachData, ...prev]);
        uploadedAttachment = attachData;
        fileDataBuffer = await file.arrayBuffer();
        toast({
          title: "Attachment uploaded",
          description: file.name,
        });
      }

      await orderApi.updateStatus(Number(id), newStatus, note);

      if (notifyCustomer && order?.customer_id && order?.customer?.email) {
        try {
          const statusText = getStatusText(newStatus);
          const attachments = [];

          if (file && fileDataBuffer) {
            try {
              const bytes = new Uint8Array(fileDataBuffer);
              const binaryString = Array.from(bytes)
                .map(byte => String.fromCharCode(byte))
                .join('');
              const base64Content = btoa(binaryString);
              
              attachments.push({
                filename: file.name,
                content: base64Content,
                type: file.type
              });
            } catch (encodeError) {
              console.error("Error encoding attachment for email:", encodeError);
            }
          }
          
          const htmlContent = `
            <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
              <h1 style="color: #0066cc;">Order Status Update</h1>
              <p>Dear ${order.customer.first_name} ${order.customer.last_name},</p>
              <p>Your order #${order.id} status has been updated to: <strong>${statusText}</strong></p>
              ${note ? `<p><strong>Note from our team:</strong> ${note}</p>` : ''}
              ${uploadedAttachment ? '<p>A document has been attached to this email for your reference.</p>' : ''}
              <p>You can view your order details by logging into your account.</p>
              <p>If you have any questions, please don't hesitate to contact us.</p>
              <p>Regards,<br>Customer Support Team</p>
            </div>
          `;

          const statusSubject = `Order Status Update: ${statusText} - Order #${order.id}`;

          // ONE mail, sent by the backend: the buyer on the To line and
          // everyone they shared the order with in Cc, so each of them sees
          // who else was told (Mauro, 23-Sep). It goes out from there rather
          // than from here because that list is the buyer's own contact
          // information and the backoffice never gets to see it.
          try {
            const { notified } = await apiRequest<{ notified: number }>(
              `${SUPABASE_URL}/orders/${order.id}/notify-shared`,
              {
                method: "POST",
                body: {
                  subject: statusSubject,
                  content: htmlContent,
                  attachments,
                },
              }
            );
            console.log(
              `Status update sent to ${order.customer.email}` +
                (notified > 0 ? `, copying ${notified} more` : "")
            );
          } catch (shareError) {
            // Write to the buyer alone rather than leave them with no notice
            // at all: they lose the Cc, not the email.
            console.error(
              "Status email via the backend failed, writing to the buyer directly:",
              shareError
            );
            await emailApi.sendAndLogEmail({
              customer_id: order.customer_id,
              to_email: order.customer.email,
              from_email: DEFAULT_SENDER_EMAIL,
              subject: statusSubject,
              content: htmlContent,
              entity_name: "orders",
              entity_id: Number(id),
              attachments: attachments
            });
          }
        } catch (emailError: any) {
          console.error("Error sending status update email:", emailError);
          toast({
            variant: "destructive",
            title: "Failed to send email notification",
            description: emailError.message || "Please check the customer's email address and try again.",
          });
        }
      }

      toast({
        title: "Status updated",
        description: `Order status has been changed to ${getStatusText(newStatus)}${notifyCustomer ? " and customer has been notified" : ""}`,
      });

      refetch();
      setIsStatusModalOpen(false);
      setStatusModalFile(null);
    } catch (err: any) {
      toast({
        variant: "destructive",
        title: "Error updating status",
        description: err.message || "Failed to update order status",
      });
    }
    setUploading(false);
  };

  const handleDownload = (docId: string) => {
    // Find the document in the order's requested documents
    const orderDocument = order?.requested_documents?.find(doc => doc.id.toString() === docId);
    if (!orderDocument) {
      toast({
        variant: "destructive",
        title: "Document not found",
        description: "Could not find the document to download.",
      });
      return;
    }

    // If document has an uploaded file, download it directly
    if (orderDocument.uploaded_file_path) {
      // The stored value is a private-bucket ref: fetch a signed link first.
      void downloadStorageFile(
        orderDocument.uploaded_file_path,
        `${orderDocument.name || `Document_${docId}`}`
      ).then((ok) => {
        if (!ok) {
          toast({
            variant: "destructive",
            title: "File not available",
            description: "Could not get a link to this file.",
          });
        }
      });

      toast({
        title: "Download started",
        description: `Downloading ${orderDocument.name || 'document'}...`,
      });
    } else {
      // If no uploaded file, show message or generate PDF (if that functionality exists)
      toast({
        variant: "destructive", 
        title: "No file available",
        description: "This document doesn't have an uploaded file to download.",
      });
    }
  };

  const handleDocumentUpdate = (updatedDocument: OrderDocument) => {
    // Trigger a refetch to get the updated order data
    refetch();
  };

  const handleUpload = async (file: File) => {
    if (!id) return;
    setUploading(true);
    try {
      // Private bucket, non-guessable name; the row stores the canonical
      // storage:// ref, opened later through storage-url.
      const { path: filePath, ref: fileRef } = newUploadTarget("order_files", `order_${id}`, file);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("order_files")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }
      
      console.log("Upload successful:", uploadData?.path);

      const { data: attachData, error: attachError } = await supabase
        .from("order_attachments")
        .insert({
          order_id: parseInt(id),
          file_path: fileRef,
          file_name: file.name
        })
        .select()
        .single();
      
      if (attachError) {
        console.error("Attachment record error:", attachError);
        throw attachError;
      }

      console.log("Attachment record created:", attachData);
      setAttachments((prev) => [attachData, ...prev]);

      toast({
        title: "Attachment uploaded",
        description: file.name,
      });
    } catch (err: any) {
      console.error("Complete upload error:", err);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: err.message || "Failed to upload attachment.",
      });
    }
    setUploading(false);
  };

  const getPropertyAddress = (order: Order): string => {
    if (!order.property) return 'No property address available';
    const { property } = order;
    return `${property.address}${property.suite_unit ? ` ${property.suite_unit}` : ''}, ${property.city}, ${property.state} ${property.zip_code}`;
  };

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </BackofficeLayout>
    );
  }

  if (error || !order) {
    return (
      <BackofficeLayout>
        <div className="space-y-6">
          <div className="flex items-center">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold tracking-tight">Order Not Found</h1>
          </div>
          <p className="text-gray-500">
            The order you're looking for could not be found. Order ID: {id}
          </p>
          <Button asChild>
            <Link to="/backoffice/orders">View All Orders</Link>
          </Button>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="mr-4">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold tracking-tight">Order #{order.id}</h1>
                {order.parent_id && (
                  <Badge variant="outline" className="text-sm">
                    Update Order
                  </Badge>
                )}
              </div>
              <div className="text-gray-500 space-y-1">
                <p>Created {new Date(order.created_at).toLocaleString()}</p>
                {order.parent_id && (
                  <p className="text-sm">
                    Original Order: 
                    <Link 
                      to={`/backoffice/orders/${order.parent_id}`}
                      className="text-blue-600 hover:text-blue-800 ml-1 underline"
                    >
                      #{order.parent_id}
                    </Link>
                  </p>
                )}
                {order.ip_address && (
                  <p className="text-sm">IP: {order.ip_address}</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={getBadgeVariant(order.status)} className="text-sm px-3 py-1">
              {getStatusText(order.status)}
            </Badge>
            {/* Dommek staff only: this opens an application holding SSN, date
                of birth and driver's licence. The endpoint enforces the same
                rule, so hiding it here is presentation, not the control. */}
            {selectedRole?.type === "platform" && (
              <>
                <Button
                variant="outline"
                onClick={openBuyerView}
                disabled={openingBuyerView}
                title="Open this order in the buyer site, read-only"
              >
                {openingBuyerView ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="h-4 w-4 mr-2" />
                )}
                Open buyer's view
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyReadOnlyLink}
                  disabled={copyingReadOnlyLink}
                  title="Copy a link that opens this order read-only, for customer support"
                >
                  {copyingReadOnlyLink ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Link2 className="h-4 w-4 mr-2" />
                  )}
                  Read-only link
                </Button>
              </>
            )}
            {canWriteOrders() && (
              <Button 
                variant="outline" 
                onClick={() => setIsStatusModalOpen(true)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Update Status
              </Button>
            )}
            {canWriteOrders() && (
              <Button
                variant="outline"
                onClick={() => setIsRefundModalOpen(true)}
                disabled={order.status === OrderStatus.refunded}
              >
                <Receipt className="h-4 w-4 mr-2" />
                Process Refund
              </Button>
            )}
            {order.status !== "draft" && order.status !== "pending_payment" && (
              <Button
                variant="outline"
                onClick={() => downloadInvoicePdf(order)}
              >
                <Download className="h-4 w-4 mr-2" />
                Download Invoice
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Property</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div>
                  <h3 className="font-medium">Association</h3>
                  <p>{order.association?.name || "Unknown Association"}</p>
                </div>
                <div>
                  <h3 className="font-medium">Property Address</h3>
                  {order.property ? (
                    <>
                      <p>
                        {order.property.address}
                        {order.property.suite_unit && ` Unit: ${order.property.suite_unit}`}
                      </p>
                      <p>
                        {order.property.city}, {order.property.state} {order.property.zip_code}
                      </p>
                    </>
                  ) : (
                    <p className="text-muted-foreground">No property address available</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Order Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium mb-2">Customer</h3>
                  {order.customer ? (
                    <div className="space-y-1">
                      <p className="text-sm">
                        Name: {order.customer.first_name} {order.customer.last_name}
                      </p>
                      <p className="text-sm">
                        Email: {order.customer.email}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        ID: {order.customer_id}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Customer ID: {order.customer_id}
                    </p>
                  )}
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between font-bold mt-4">
                    <span>Total</span>
                    <span>${order.total_amount?.toFixed(2)}</span>
                  </div>
                  {order.refunds && order.refunds.length > 0 && (
                    <div className="flex justify-between text-red-500 mt-2">
                      <span>Total Refunded</span>
                      <span>-${order.refunds.reduce((sum, refund) => sum + refund.amount, 0).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Order Details</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid grid-cols-8 mb-8">
                <TabsTrigger value="items" className="flex items-center">
                  <Package className="h-4 w-4 mr-2" />
                  <span>Order Items</span>
                </TabsTrigger>
                <TabsTrigger value="documents" className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Requested Documents</span>
                </TabsTrigger>
                <TabsTrigger value="payments" className="flex items-center">
                  <CreditCard className="h-4 w-4 mr-2" />
                  <span>Payments</span>
                </TabsTrigger>
                <TabsTrigger value="refunds" className="flex items-center">
                  <Receipt className="h-4 w-4 mr-2" />
                  <span>Refunds</span>
                </TabsTrigger>
                <TabsTrigger value="emails" className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>Emails Sent</span>
                </TabsTrigger>
                <TabsTrigger value="attachments" className="flex items-center">
                  <Upload className="h-4 w-4 mr-2" />
                  <span>Attachments</span>
                </TabsTrigger>
                <TabsTrigger value="notes" className="flex items-center">
                  <Pen className="h-4 w-4 mr-2" />
                  <span>Notes</span>
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center">
                  <History className="h-4 w-4 mr-2" />
                  <span>Order History</span>
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="items">
                <OrderItemsTab
                  items={order?.items || []}
                  atClosingItems={order?.at_closing_items || []}
                  totalAmount={order?.total_amount || 0}
                  isDraft={order?.status === 'draft'}
                  isSaving={isSavingItems}
                  onSaveItems={handleSaveItems}
                />
              </TabsContent>
              
              <TabsContent value="documents">
                <RequestedDocumentsTab 
                  documents={order.requested_documents || []} 
                  orderStatus={order.status}
                  onDownload={handleDownload}
                  orderDate={order.created_at.toString()}
                  propertyAddress={getPropertyAddress(order)}
                  associationName={order.association?.name}
                  ipAddress={order.ip_address}
                  onDocumentUpdate={handleDocumentUpdate}
                />
              </TabsContent>
              
              <TabsContent value="payments">
                <PaymentsTab orderId={Number(id)} />
              </TabsContent>
              
              <TabsContent value="refunds">
                {order.refunds && order.refunds.length > 0 ? (
                  <div className="space-y-4">
                    <div className="rounded-md border">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="text-left p-3">Refund ID</th>
                            <th className="text-left p-3">Date</th>
                            <th className="text-right p-3">Amount</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Items</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.refunds.map((refund) => (
                            <tr key={refund.id} className="border-t">
                              <td className="p-3">{refund.id}</td>
                              <td className="p-3">{new Date(refund.created_at).toLocaleDateString()}</td>
                              <td className="p-3 text-right text-red-500">-${refund.amount.toFixed(2)}</td>
                              <td className="p-3">
                                <Badge variant={refund.status === 'completed' ? 'success' : 'warning'}>
                                  {refund.status}
                                </Badge>
                              </td>
                              <td className="p-3">
                                {refund.items.map(item => (
                                  <div key={item.item_id} className="text-sm">
                                    {item.quantity}x Item #{item.item_id}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="border-t bg-muted/50">
                            <td className="p-3 font-medium" colSpan={2}>Total Refunded</td>
                            <td className="p-3 text-right font-medium text-red-500">
                              -${order.refunds.reduce((sum, refund) => sum + refund.amount, 0).toFixed(2)}
                            </td>
                            <td className="p-3" colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Receipt className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg font-medium">No Refunds</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      This order hasn't had any refunds processed yet.
                    </p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="emails">
                <EmailHistoryTab orderId={Number(id)} />
              </TabsContent>
              
              <TabsContent value="attachments">
                <AttachmentsTab
                  orderId={Number(id)}
                  attachments={attachments}
                  onUpload={handleUpload}
                  uploading={uploading}
                  setAttachments={setAttachments}
                />
              </TabsContent>
              
              <TabsContent value="notes">
                <OrderNotesTab orderId={Number(id)} />
              </TabsContent>
              
              <TabsContent value="history">
                <OrderHistoryTab statusHistory={order.status_history || []} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      <StatusUpdateModal
        isOpen={isStatusModalOpen}
        onClose={() => {
          setIsStatusModalOpen(false);
          setStatusModalFile(null);
        }}
        currentStatus={order?.status}
        onUpdate={(status, note, notifyCustomer) =>
          handleStatusUpdate(status, note, notifyCustomer, statusModalFile)
        }
        notifyCustomer={true}
        uploading={uploading}
        file={statusModalFile}
        setFile={setStatusModalFile}
      />

      <RefundModal
        isOpen={isRefundModalOpen}
        onClose={() => setIsRefundModalOpen(false)}
        items={order?.items || []}
        orderId={Number(id)}
        refunds={order?.refunds || []}
        onRefundComplete={() => {
          refetch();
          setIsRefundModalOpen(false);
        }}
      />
      
      <DialogFixUtil open={isStatusModalOpen || isRefundModalOpen} />
    </BackofficeLayout>
  );
};

export default OrderDetail;
